// V3-1-2 reference: UC-A · ADR-0023 (AI Disclosure schema) ·
// ADR-0022 §5 as amended 2026-09-07 (grants auto-advance) ·
// migration 066-ai-disclosures.sql / .pg.sql
import { canonicalJson, generateRandomId, sha256Hex } from '../utils/audit-crypto.js';
import { resolveActiveGrant } from './supplier-portfolio-service.js';
import { getDataLineage, lineageDisclosureFields, validateLineageGraph } from './data-lineage-service.js';
import { SCOPE_CLASSES } from './agent-authority-service.js';
export const DISCLOSURE_SCHEMA_VERSIONS = ['1.0', '1.1'];
/** The four groups a publish cannot omit. Exported so the SPA wizard
 *  (V3-1-5) and the scorecard (V3-2-6) read the same list this
 *  validator enforces, rather than restating it. */
export const REQUIRED_CONTENT_GROUPS = [
    'scope',
    'models',
    'providers',
    'training_data_classes',
];
/** Carries a code so routes map cleanly: `not_found` → 404 (never 403,
 *  per the default-deny posture), the rest → 400. */
export class DisclosureError extends Error {
    code;
    errors;
    constructor(code, message, errors = []) {
        super(message);
        this.name = 'DisclosureError';
        this.code = code;
        this.errors = errors;
    }
}
// ------------------------------------------------------------------
// Validation
// ------------------------------------------------------------------
function isPlainObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function nonEmptyString(v) {
    return typeof v === 'string' && v.trim().length > 0;
}
function err(out, path, code, message) {
    out.push({ path, code, message });
}
function requireStringField(out, obj, path, field) {
    if (!nonEmptyString(obj[field])) {
        err(out, `${path}.${field}`, 'required_string', `${field} is required and must be a non-empty string`);
    }
}
function requireBooleanField(out, obj, path, field) {
    if (typeof obj[field] !== 'boolean') {
        err(out, `${path}.${field}`, 'required_boolean', `${field} is required and must be a boolean — an unanswered question is not "false"`);
    }
}
const HOSTING_MODES = [
    'self_hosted',
    'private_cloud',
    'third_party_api',
    'embedded',
];
const OVERSIGHT_MODES = [
    'human_in_the_loop',
    'human_on_the_loop',
    'none',
];
const CLAIM_STATUSES = [
    'certified',
    'assessed',
    'aligned',
    'planned',
];
const EVIDENCE_REF_KINDS = [
    'evidence_artifact',
    'external_url',
];
/**
 * Validate `content_json` against the nine-group schema.
 *
 * Required to publish (the CISA AI SBOM minimum elements mapped onto
 * ADR-0023 §2's groups): `scope`, `models[]`, `providers[]`,
 * `training_data_classes[]`. Author and timestamp — the other two CISA
 * minimum elements — are columns (`published_by_user_id`,
 * `published_at`), not content fields.
 *
 * `providers[]` must be PRESENT but may be empty: a supplier running
 * only self-hosted models has no AI sub-processor, and forcing a row
 * there would manufacture a disclosure fact. It becomes non-empty the
 * moment any model's `hosting_mode` is not `self_hosted`.
 *
 * The remaining five groups are optional (owner decision 1) but are
 * fully validated when present — a half-filled optional group is a
 * worse artifact than an absent one.
 *
 * Errors are returned sorted by `path` so the result is deterministic
 * for snapshots and for the wizard's field-level rendering.
 */
export function validateDisclosureContent(content) {
    const errors = [];
    if (!isPlainObject(content)) {
        return {
            valid: false,
            errors: [
                {
                    path: '',
                    code: 'invalid_root',
                    message: 'content must be a JSON object',
                },
            ],
        };
    }
    // --- scope (required) ---
    const scope = content.scope;
    if (!isPlainObject(scope)) {
        err(errors, 'scope', 'required_group', 'scope is required to publish');
    }
    else {
        const products = scope.products_services;
        if (!Array.isArray(products) || products.length === 0) {
            err(errors, 'scope.products_services', 'required_non_empty_array', 'scope.products_services must name at least one product or service');
        }
        else {
            products.forEach((p, i) => {
                if (!nonEmptyString(p)) {
                    err(errors, `scope.products_services[${i}]`, 'required_string', 'each product or service must be a non-empty string');
                }
            });
        }
    }
    // --- models[] (required, non-empty) ---
    const models = content.models;
    if (!Array.isArray(models) || models.length === 0) {
        err(errors, 'models', 'required_non_empty_array', 'models must list at least one model to publish');
    }
    else {
        models.forEach((m, i) => {
            const path = `models[${i}]`;
            if (!isPlainObject(m)) {
                err(errors, path, 'invalid_entry', 'each model must be an object');
                return;
            }
            requireStringField(errors, m, path, 'name');
            requireStringField(errors, m, path, 'version');
            requireStringField(errors, m, path, 'provider');
            requireStringField(errors, m, path, 'purpose');
            if (typeof m.hosting_mode !== 'string' ||
                !HOSTING_MODES.includes(m.hosting_mode)) {
                err(errors, `${path}.hosting_mode`, 'invalid_enum', `hosting_mode must be one of ${HOSTING_MODES.join(', ')}`);
            }
        });
    }
    // --- providers[] (required to be present; may be empty) ---
    const providers = content.providers;
    if (!Array.isArray(providers)) {
        err(errors, 'providers', 'required_array', 'providers must be present (an empty array declares "no AI sub-processors")');
    }
    else {
        providers.forEach((p, i) => {
            const path = `providers[${i}]`;
            if (!isPlainObject(p)) {
                err(errors, path, 'invalid_entry', 'each provider must be an object');
                return;
            }
            requireStringField(errors, p, path, 'name');
            requireStringField(errors, p, path, 'domain');
            requireStringField(errors, p, path, 'data_sent');
            requireStringField(errors, p, path, 'region');
        });
        // Cross-check: a model that is not self-hosted has a third party in
        // its data path, so at least one sub-processor must be named.
        if (providers.length === 0 &&
            Array.isArray(models) &&
            models.some((m) => isPlainObject(m) &&
                typeof m.hosting_mode === 'string' &&
                m.hosting_mode !== 'self_hosted')) {
            err(errors, 'providers', 'required_non_empty_array', 'providers must name at least one AI sub-processor when any model is not self_hosted');
        }
    }
    // --- training_data_classes[] (required, non-empty) ---
    const classes = content.training_data_classes;
    if (!Array.isArray(classes) || classes.length === 0) {
        err(errors, 'training_data_classes', 'required_non_empty_array', 'training_data_classes must describe at least one class of training data');
    }
    else {
        classes.forEach((c, i) => {
            const path = `training_data_classes[${i}]`;
            if (!isPlainObject(c)) {
                err(errors, path, 'invalid_entry', 'each entry must be an object');
                return;
            }
            requireStringField(errors, c, path, 'category');
            requireStringField(errors, c, path, 'provenance');
            requireBooleanField(errors, c, path, 'includes_customer_data');
        });
    }
    // --- optional groups: validated only when present ---
    const retention = content.retention_and_training_use;
    if (retention !== undefined && retention !== null) {
        const path = 'retention_and_training_use';
        if (!isPlainObject(retention)) {
            err(errors, path, 'invalid_group', 'must be an object when present');
        }
        else {
            requireStringField(errors, retention, path, 'retention_window');
            requireBooleanField(errors, retention, path, 'customer_data_trains_models');
            requireBooleanField(errors, retention, path, 'opt_out_available');
        }
    }
    const oversight = content.human_oversight;
    if (oversight !== undefined && oversight !== null) {
        const path = 'human_oversight';
        if (!isPlainObject(oversight)) {
            err(errors, path, 'invalid_group', 'must be an object when present');
        }
        else {
            requireStringField(errors, oversight, path, 'override_path');
            const perModel = oversight.per_model;
            if (!Array.isArray(perModel) || perModel.length === 0) {
                err(errors, `${path}.per_model`, 'required_non_empty_array', 'human_oversight.per_model must cover at least one model when the group is filled');
            }
            else {
                perModel.forEach((entry, i) => {
                    const p = `${path}.per_model[${i}]`;
                    if (!isPlainObject(entry)) {
                        err(errors, p, 'invalid_entry', 'each entry must be an object');
                        return;
                    }
                    requireStringField(errors, entry, p, 'model_name');
                    if (typeof entry.oversight_mode !== 'string' ||
                        !OVERSIGHT_MODES.includes(entry.oversight_mode)) {
                        err(errors, `${p}.oversight_mode`, 'invalid_enum', `oversight_mode must be one of ${OVERSIGHT_MODES.join(', ')}`);
                    }
                });
            }
        }
    }
    const incident = content.incident;
    if (incident !== undefined && incident !== null) {
        const path = 'incident';
        if (!isPlainObject(incident)) {
            err(errors, path, 'invalid_group', 'must be an object when present');
        }
        else {
            requireStringField(errors, incident, path, 'contact');
            requireStringField(errors, incident, path, 'process');
            requireStringField(errors, incident, path, 'notification_sla');
        }
    }
    const frameworks = content.frameworks_claimed;
    if (frameworks !== undefined && frameworks !== null) {
        if (!Array.isArray(frameworks)) {
            err(errors, 'frameworks_claimed', 'invalid_group', 'must be an array when present');
        }
        else {
            frameworks.forEach((f, i) => {
                const path = `frameworks_claimed[${i}]`;
                if (!isPlainObject(f)) {
                    err(errors, path, 'invalid_entry', 'each entry must be an object');
                    return;
                }
                requireStringField(errors, f, path, 'framework_id');
                if (typeof f.claim_status !== 'string' ||
                    !CLAIM_STATUSES.includes(f.claim_status)) {
                    err(errors, `${path}.claim_status`, 'invalid_enum', `claim_status must be one of ${CLAIM_STATUSES.join(', ')}`);
                }
            });
        }
    }
    const evidence = content.evidence_refs;
    if (evidence !== undefined && evidence !== null) {
        if (!Array.isArray(evidence)) {
            err(errors, 'evidence_refs', 'invalid_group', 'must be an array when present');
        }
        else {
            evidence.forEach((e, i) => {
                const path = `evidence_refs[${i}]`;
                if (!isPlainObject(e)) {
                    err(errors, path, 'invalid_entry', 'each entry must be an object');
                    return;
                }
                if (typeof e.kind !== 'string' || !EVIDENCE_REF_KINDS.includes(e.kind)) {
                    err(errors, `${path}.kind`, 'invalid_enum', `kind must be one of ${EVIDENCE_REF_KINDS.join(', ')}`);
                }
                requireStringField(errors, e, path, 'ref');
            });
        }
    }
    validateAuthorityGroups(content, errors);
    if (content.data_lineage !== undefined) {
        try {
            validateLineageGraph(content.data_lineage);
        }
        catch (e) {
            err(errors, 'data_lineage', 'invalid_group', String(e));
        }
    }
    if (content.data_sources !== undefined) {
        try {
            if (!Array.isArray(content.data_sources))
                throw new Error('Data sources must be an array');
            validateLineageGraph({ nodes: content.data_sources.map(n => ({ ...n, type: 'dataset' })), edges: [] });
        }
        catch (e) {
            err(errors, 'data_sources', 'invalid_group', String(e));
        }
    }
    errors.sort((a, b) => a.path === b.path
        ? a.code.localeCompare(b.code)
        : a.path.localeCompare(b.path));
    return { valid: errors.length === 0, errors };
}
const AUTHORITY_GROUPS = ['tools', 'action_ceiling', 'containment'];
const CEILING_LIMITS = ['spend_usd', 'rows', 'recipients'];
/** schema_version + the 1.1 agent-authority groups (validated only when present). */
function validateAuthorityGroups(content, errors) {
    const sv = content.schema_version;
    if (sv !== undefined && (typeof sv !== 'string' || !DISCLOSURE_SCHEMA_VERSIONS.includes(sv))) {
        err(errors, 'schema_version', 'invalid_enum', `schema_version must be one of ${DISCLOSURE_SCHEMA_VERSIONS.join(', ')}`);
        return;
    }
    const present = AUTHORITY_GROUPS.filter((g) => content[g] !== undefined && content[g] !== null);
    if (present.length === 0)
        return;
    if (sv !== '1.1') {
        for (const g of present)
            err(errors, g, 'requires_schema_1_1', `${g} needs schema_version "1.1"`);
        return;
    }
    const isScope = (v) => typeof v === 'string' && SCOPE_CLASSES.includes(v);
    const tools = content.tools;
    if (tools !== undefined && tools !== null) {
        if (!Array.isArray(tools))
            err(errors, 'tools', 'invalid_group', 'must be an array when present');
        else
            tools.forEach((t, i) => {
                const path = `tools[${i}]`;
                if (!isPlainObject(t))
                    return err(errors, path, 'invalid_entry', 'each tool must be an object');
                if (!nonEmptyString(t.name) || t.name.length > 200)
                    err(errors, `${path}.name`, 'required_string', 'each tool needs a name (200 characters or fewer)');
                if (!isScope(t.scope_class))
                    err(errors, `${path}.scope_class`, 'invalid_enum', `scope_class must be one of ${SCOPE_CLASSES.join(', ')}`);
            });
    }
    const ceiling = content.action_ceiling;
    if (ceiling !== undefined && ceiling !== null) {
        if (!isPlainObject(ceiling))
            err(errors, 'action_ceiling', 'invalid_group', 'must be an object when present');
        else {
            const classes = ceiling.authorized_action_classes;
            if (classes !== undefined && (!Array.isArray(classes) || classes.some((c) => !isScope(c)))) {
                err(errors, 'action_ceiling.authorized_action_classes', 'invalid_enum', `each class must be one of ${SCOPE_CLASSES.join(', ')}`);
            }
            const prohibited = ceiling.prohibited_actions;
            if (prohibited !== undefined && (!Array.isArray(prohibited) || prohibited.some((p) => !nonEmptyString(p)))) {
                err(errors, 'action_ceiling.prohibited_actions', 'invalid_entry', 'each prohibited action must be a non-empty string');
            }
            const limits = ceiling.limits;
            if (limits !== undefined) {
                if (!isPlainObject(limits))
                    err(errors, 'action_ceiling.limits', 'invalid_group', 'must be an object when present');
                else
                    for (const k of Object.keys(limits)) {
                        const v = limits[k];
                        if (!CEILING_LIMITS.includes(k))
                            err(errors, `action_ceiling.limits.${k}`, 'unknown_field', `limits may set ${CEILING_LIMITS.join(', ')}`);
                        else if (typeof v !== 'number' || !Number.isFinite(v) || v < 0)
                            err(errors, `action_ceiling.limits.${k}`, 'invalid_number', 'must be a non-negative number');
                    }
            }
        }
    }
    const containment = content.containment;
    if (containment !== undefined && containment !== null) {
        if (!isPlainObject(containment))
            err(errors, 'containment', 'invalid_group', 'must be an object when present');
        else {
            for (const k of ['kill_switch_kind', 'fallback_mode']) {
                if (containment[k] !== undefined && !nonEmptyString(containment[k]))
                    err(errors, `containment.${k}`, 'required_string', `${k} must be a non-empty string when present`);
            }
            if (containment.rollback_documented !== undefined && typeof containment.rollback_documented !== 'boolean') {
                err(errors, 'containment.rollback_documented', 'invalid_type', 'rollback_documented must be true or false');
            }
        }
    }
}
/**
 * Prefill for a 1.1 disclosure from a system's APPROVED declared authority
 * (ADR-0092 §1: intake and disclosure read the same shape). Never invents a
 * value: empty groups stay absent. The supplier still reviews and publishes.
 */
export function disclosureAuthorityFromDeclaration(v) {
    const parse = (s, d) => { try {
        return JSON.parse(s) ?? d;
    }
    catch {
        return d;
    } };
    const tools = parse(v.tools_json, []);
    const c = parse(v.action_ceiling_json, {});
    const k = parse(v.containment_json, {});
    const out = { schema_version: '1.1' };
    if (tools.length)
        out.tools = tools.map((t) => ({ name: t.name, scope_class: t.scope_class, ...(t.provider_key ? { provider_key: t.provider_key } : {}) }));
    const ceiling = {};
    if (c.authorized_action_classes?.length)
        ceiling.authorized_action_classes = [...c.authorized_action_classes];
    if (c.prohibited_actions?.length)
        ceiling.prohibited_actions = [...c.prohibited_actions];
    if (c.limits && Object.keys(c.limits).length)
        ceiling.limits = { ...c.limits };
    if (Object.keys(ceiling).length)
        out.action_ceiling = ceiling;
    const containment = {};
    if (k.kill_switch_kind)
        containment.kill_switch_kind = k.kill_switch_kind;
    if (k.rollback_evidence_ref)
        containment.rollback_documented = true;
    if (k.fallback_mode)
        containment.fallback_mode = k.fallback_mode;
    if (Object.keys(containment).length)
        out.containment = containment;
    return out;
}
// ------------------------------------------------------------------
// Hashing
// ------------------------------------------------------------------
/**
 * SHA-256 over canonical JSON of the content — sorted keys at every
 * level, no insignificant whitespace, UTF-8 (ADR-0023 §3). Reuses
 * `canonicalJson` from `utils/audit-crypto.ts` so a disclosure hash is
 * reproducible with the same rules an auditor already applies to the
 * audit chain and to `assessment_packages.canonical_json_sha256`.
 *
 * The stored `content_json` is the canonical string itself, so a
 * reader re-derives the hash from exactly the bytes it was served.
 */
export async function computeContentHash(content) {
    const canonical = canonicalJson(content);
    return { canonical, sha256: await sha256Hex(canonical) };
}
// ------------------------------------------------------------------
// Create
// ------------------------------------------------------------------
export async function createDisclosure(db, input) {
    // Default-deny on an unscoped or unattributed record: the column
    // NOT NULLs only stop `null`, and an empty workspace id would create
    // a disclosure owned by no tenant.
    if (!nonEmptyString(input.workspace_id)) {
        throw new DisclosureError('invalid_input', 'createDisclosure: workspace_id is required — disclosures are workspace-scoped.');
    }
    if (!nonEmptyString(input.created_by_user_id)) {
        throw new DisclosureError('invalid_input', 'createDisclosure: created_by_user_id is required — provenance is not optional.');
    }
    if (!nonEmptyString(input.title)) {
        throw new DisclosureError('invalid_input', 'createDisclosure: title is required.');
    }
    // ADR-0023 owner decision 2: many disclosures per workspace, each
    // with a required scope, so a request can name the product it
    // concerns.
    if (!nonEmptyString(input.scope_description)) {
        throw new DisclosureError('invalid_input', 'createDisclosure: scope_description is required — a disclosure states what it covers.');
    }
    const disclosureId = input.disclosure_id ?? generateRandomId('disc');
    if (input.system_id !== undefined) {
        const system = await db.query('SELECT system_id FROM ai_systems WHERE system_id = ? AND workspace_id = ?', [input.system_id, input.workspace_id]);
        if (!system.results.length)
            throw new DisclosureError('not_found', 'System not found in this workspace.');
    }
    const now = new Date().toISOString();
    await db.execute(`INSERT INTO ai_disclosures (
       disclosure_id, workspace_id, title, scope_description,
       current_version_id, created_by_user_id, created_at, updated_at, system_id
     ) VALUES (?, ?, ?, ?, NULL, ?, ?, ?, ?)`, [
        disclosureId,
        input.workspace_id,
        input.title,
        input.scope_description,
        input.created_by_user_id,
        now,
        now,
        input.system_id ?? null,
    ]);
    return {
        disclosure_id: disclosureId,
        workspace_id: input.workspace_id,
        title: input.title,
        scope_description: input.scope_description,
        current_version_id: null,
        created_by_user_id: input.created_by_user_id,
        created_at: now,
        updated_at: now,
    };
}
/** Workspace-scoped read of the standing record. `null` → caller 404s. */
export async function getDisclosure(db, scope) {
    if (!nonEmptyString(scope.workspace_id) ||
        !nonEmptyString(scope.disclosure_id)) {
        return null;
    }
    const result = await db.query(`SELECT * FROM ai_disclosures
      WHERE disclosure_id = ? AND workspace_id = ?`, [scope.disclosure_id, scope.workspace_id]);
    return result.results[0] ?? null;
}
export async function listDisclosures(db, workspaceId) {
    if (!nonEmptyString(workspaceId))
        return [];
    const result = await db.query(`SELECT * FROM ai_disclosures
      WHERE workspace_id = ?
      ORDER BY created_at DESC, disclosure_id ASC`, [workspaceId]);
    return result.results;
}
export async function publishVersion(db, audit, input) {
    if (!nonEmptyString(input.published_by_user_id)) {
        throw new DisclosureError('invalid_input', 'publishVersion: published_by_user_id is required — every version records who published it.');
    }
    // Workspace scoping is the caller's session, not the id in the body:
    // the disclosure must already belong to the resolved workspace.
    // Miss → not_found (404), never 403, so a probe cannot distinguish
    // "wrong tenant" from "no such disclosure".
    const disclosure = await getDisclosure(db, {
        workspace_id: input.workspace_id,
        disclosure_id: input.disclosure_id,
    });
    if (!disclosure) {
        throw new DisclosureError('not_found', 'publishVersion: disclosure not found in this workspace.');
    }
    const validation = validateDisclosureContent(input.content);
    if (!validation.valid) {
        throw new DisclosureError('invalid_content', `publishVersion: content fails the ADR-0023 minimum elements (${validation.errors.length} error(s)).`, validation.errors);
    }
    // Capture the saved graph at publication time; prior versions remain immutable.
    const linked = await db.query('SELECT system_id FROM ai_disclosures WHERE disclosure_id = ? AND workspace_id = ?', [input.disclosure_id, input.workspace_id]);
    let content = input.content;
    if (linked.results[0]?.system_id) {
        const lineage = await getDataLineage(db, input.workspace_id, linked.results[0].system_id);
        if (lineage.revision > 0)
            content = { ...content, ...lineageDisclosureFields(lineage.graph) };
    }
    const { canonical, sha256 } = await computeContentHash(content);
    // version_no = max + 1. UNIQUE(disclosure_id, version_no) in
    // migration 066 is the real guard: a concurrent publish that picked
    // the same number loses the INSERT and the caller retries.
    const maxRow = await db.query(`SELECT MAX(version_no) AS max_no FROM ai_disclosure_versions
      WHERE disclosure_id = ?`, [input.disclosure_id]);
    const versionNo = (maxRow.results[0]?.max_no ?? 0) + 1;
    // ADR-0023 §4: attestation requires BOTH a supplier_attested source
    // AND an active membership in the disclosure's OWN workspace. An
    // enterprise upload attests "I uploaded this", which is recorded by
    // `source` + `published_by_user_id`, never by these columns
    // (ADR-0023 §6, owner decision 4).
    let isMember = false;
    if (input.source === 'supplier_attested' && input.membershipCheck) {
        isMember = Boolean(await input.membershipCheck({
            workspace_id: disclosure.workspace_id,
            user_id: input.published_by_user_id,
        }));
    }
    const attests = input.source === 'supplier_attested' && input.attested !== false && isMember;
    const now = new Date().toISOString();
    const versionId = input.version_id ?? generateRandomId('dver');
    const attestedBy = attests ? input.published_by_user_id : null;
    const attestedAt = attests ? now : null;
    await db.execute(`INSERT INTO ai_disclosure_versions (
       version_id, disclosure_id, version_no, content_json,
       canonical_json_sha256, source, attested_by_user_id, attested_at,
       provider_id, model_id, questionnaire_id,
       published_at, published_by_user_id
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [
        versionId,
        input.disclosure_id,
        versionNo,
        canonical,
        sha256,
        input.source,
        attestedBy,
        attestedAt,
        input.provider_id ?? null,
        input.model_id ?? null,
        input.questionnaire_id ?? null,
        now,
        input.published_by_user_id,
    ]);
    // The pointer moves; the versions never do.
    await db.execute(`UPDATE ai_disclosures
        SET current_version_id = ?, updated_at = ?
      WHERE disclosure_id = ? AND workspace_id = ?`, [versionId, now, input.disclosure_id, input.workspace_id]);
    // ADR-0022 §5 (amended 2026-09-07): every ACTIVE grant whose
    // content_ref names an earlier version of THIS disclosure advances to
    // the new one, so enterprise drift alerts (V3-2-7) re-score rather
    // than re-request. Defensibility is preserved because every scorecard
    // run pins the version_id it scored and every version stays readable
    // by hash.
    //
    // Only a genuinely attested supplier version advances a grant. Drafts
    // and enterprise_upload_* versions never do — an enterprise must not
    // be able to move a supplier's grant by uploading a document.
    const advancedGrantIds = attests
        ? await advanceGrantsToVersion(db, {
            disclosure_id: input.disclosure_id,
            supplier_workspace_id: disclosure.workspace_id,
            new_version_id: versionId,
        })
        : [];
    // Exactly one audit event per publish (ADR-0014 / ADR-0023 §1). The
    // grant advance rides on it rather than emitting a second event, so
    // "what happened at this publish" is one chained row.
    if (audit) {
        await audit.emit({
            workspaceId: disclosure.workspace_id,
            eventType: 'disclosure.published',
            actorType: 'user',
            actorId: input.published_by_user_id,
            targetKind: 'ai_disclosure',
            targetId: input.disclosure_id,
            before: disclosure.current_version_id
                ? { current_version_id: disclosure.current_version_id }
                : null,
            after: {
                version_id: versionId,
                version_no: versionNo,
                source: input.source,
                canonical_json_sha256: sha256,
                attested: attests,
                attested_by_user_id: attestedBy,
                provider_id: input.provider_id ?? null,
                model_id: input.model_id ?? null,
                questionnaire_id: input.questionnaire_id ?? null,
                advanced_grant_ids: advancedGrantIds,
            },
        });
    }
    return {
        version: {
            version_id: versionId,
            disclosure_id: input.disclosure_id,
            version_no: versionNo,
            content_json: canonical,
            canonical_json_sha256: sha256,
            source: input.source,
            attested_by_user_id: attestedBy,
            attested_at: attestedAt,
            provider_id: input.provider_id ?? null,
            model_id: input.model_id ?? null,
            questionnaire_id: input.questionnaire_id ?? null,
            published_at: now,
            published_by_user_id: input.published_by_user_id,
        },
        content,
        advanced_grant_ids: advancedGrantIds,
    };
}
/**
 * Move active `live_workspace_read` grants from any earlier version of
 * this disclosure onto `new_version_id`.
 *
 * Scoped three ways so it can never touch an unrelated grant:
 *   - the grant names this supplier workspace,
 *   - the grant is not revoked,
 *   - the grant's `content_ref` is a version_id of THIS disclosure.
 *
 * A revoked grant is left exactly as it was — revocation is the
 * supplier's kill switch and a later publish must not resurrect it.
 */
async function advanceGrantsToVersion(db, scope) {
    const candidates = await db.query(`SELECT g.grant_id AS grant_id
       FROM supplier_grants g
       JOIN ai_disclosure_versions v ON v.version_id = g.content_ref
      WHERE g.supplier_workspace_id = ?
        AND g.revoked_at IS NULL
        AND g.grant_type = 'live_workspace_read'
        AND v.disclosure_id = ?
        AND g.content_ref <> ?
      ORDER BY g.grant_id ASC`, [scope.supplier_workspace_id, scope.disclosure_id, scope.new_version_id]);
    const ids = candidates.results.map((r) => r.grant_id);
    for (const grantId of ids) {
        await db.execute(`UPDATE supplier_grants
          SET content_ref = ?, last_synced_at = ?
        WHERE grant_id = ? AND revoked_at IS NULL`, [scope.new_version_id, new Date().toISOString(), grantId]);
    }
    return ids;
}
// ------------------------------------------------------------------
// Reads
// ------------------------------------------------------------------
/** Workspace-scoped single-version read. `null` → caller 404s. */
export async function getVersion(db, scope) {
    if (!nonEmptyString(scope.workspace_id) ||
        !nonEmptyString(scope.version_id)) {
        return null;
    }
    const result = await db.query(`SELECT v.* FROM ai_disclosure_versions v
       JOIN ai_disclosures d ON d.disclosure_id = v.disclosure_id
      WHERE v.version_id = ? AND d.workspace_id = ?`, [scope.version_id, scope.workspace_id]);
    return result.results[0] ?? null;
}
/** Workspace-scoped version history, newest first. */
export async function listVersions(db, scope) {
    if (!nonEmptyString(scope.workspace_id) ||
        !nonEmptyString(scope.disclosure_id)) {
        return [];
    }
    const result = await db.query(`SELECT v.* FROM ai_disclosure_versions v
       JOIN ai_disclosures d ON d.disclosure_id = v.disclosure_id
      WHERE v.disclosure_id = ? AND d.workspace_id = ?
      ORDER BY v.version_no DESC`, [scope.disclosure_id, scope.workspace_id]);
    return result.results;
}
/**
 * The one sanctioned cross-tenant read (ADR-0013 §13.4, ADR-0023 §7).
 *
 * Resolves the enterprise org's ACTIVE `live_workspace_read` grant on
 * the supplier workspace and returns the version its `content_ref`
 * names — never `current_version_id`, so a supplier's unattested or
 * draft work is not readable through a grant. Grants that follow the
 * latest attested version get there by `advanceGrantsToVersion`, not by
 * the reader dereferencing a pointer.
 *
 * Default-deny at every step: no org id, no supplier id, no grant, a
 * revoked grant, a grant with no `content_ref`, or a `content_ref`
 * pointing outside the supplier's workspace all return `null`, which
 * the caller renders as 404.
 */
export async function getVersionForGrant(db, scope) {
    const grant = await resolveActiveGrant(db, {
        enterprise_organization_id: scope.enterprise_organization_id,
        supplier_workspace_id: scope.supplier_workspace_id,
        grant_type: 'live_workspace_read',
    });
    if (!grant?.content_ref)
        return null;
    // Re-scope the version read to the supplier workspace the grant
    // names. A `content_ref` that somehow points at another tenant's
    // version resolves to nothing rather than leaking it.
    return getVersion(db, {
        workspace_id: scope.supplier_workspace_id,
        version_id: grant.content_ref,
    });
}
/**
 * Public, UNAUTHENTICATED hash verify (ADR-0023 owner decision 3).
 *
 * Returns publisher workspace, publish date and version number for a
 * supplied `canonical_json_sha256` — and nothing else. Content stays
 * grant-scoped; a hash holder learns only that this exact document was
 * published, by whom, and when. Unknown hash → `null`, which the route
 * renders as the same 404 as any other miss.
 */
export async function verifyHash(db, sha256) {
    if (!nonEmptyString(sha256))
        return null;
    const result = await db.query(`SELECT d.workspace_id AS workspace_id,
            v.published_at AS published_at,
            v.version_no AS version_no
       FROM ai_disclosure_versions v
       JOIN ai_disclosures d ON d.disclosure_id = v.disclosure_id
      WHERE v.canonical_json_sha256 = ?
      ORDER BY v.published_at ASC
      LIMIT 1`, [sha256]);
    const row = result.results[0];
    if (!row)
        return null;
    // Project explicitly — never spread the row, or a future column
    // starts leaking through the public endpoint.
    return {
        workspace_id: row.workspace_id,
        published_at: row.published_at,
        version_no: row.version_no,
    };
}
/**
 * Flatten a content document to leaf paths so the diff is field-level
 * rather than "the JSON changed". Arrays are indexed
 * (`models[0].name`) — array order is meaningful in the canonical form,
 * and a reordered list IS a different document by hash, so the diff
 * must say so.
 *
 * An empty object or array is emitted as its own leaf, otherwise
 * clearing a list would produce no diff rows at all.
 */
function flattenLeaves(value, prefix, out) {
    if (Array.isArray(value)) {
        if (value.length === 0) {
            out.set(prefix, []);
            return;
        }
        value.forEach((v, i) => flattenLeaves(v, `${prefix}[${i}]`, out));
        return;
    }
    if (isPlainObject(value)) {
        const keys = Object.keys(value)
            .filter((k) => value[k] !== undefined)
            .sort();
        if (keys.length === 0) {
            out.set(prefix, {});
            return;
        }
        for (const k of keys) {
            flattenLeaves(value[k], prefix ? `${prefix}.${k}` : k, out);
        }
        return;
    }
    out.set(prefix, value ?? null);
}
/**
 * Deterministic field-level diff between two versions of the SAME
 * disclosure. Both versions are read workspace-scoped first, so this
 * can never diff across tenants; a missing or foreign version is
 * `not_found`.
 *
 * Determinism: leaves are compared on a lexicographically sorted union
 * of both documents' paths, and equality is canonical-JSON equality —
 * so key order and whitespace in the stored content can never register
 * as a change.
 */
export async function diffVersions(db, scope) {
    const from = await getVersion(db, {
        workspace_id: scope.workspace_id,
        version_id: scope.from_version_id,
    });
    const to = await getVersion(db, {
        workspace_id: scope.workspace_id,
        version_id: scope.to_version_id,
    });
    if (!from || !to) {
        throw new DisclosureError('not_found', 'diffVersions: one or both versions were not found in this workspace.');
    }
    if (from.disclosure_id !== to.disclosure_id) {
        throw new DisclosureError('invalid_input', 'diffVersions: both versions must belong to the same disclosure.');
    }
    return {
        from_version_id: from.version_id,
        to_version_id: to.version_id,
        from_version_no: from.version_no,
        to_version_no: to.version_no,
        changes: diffContent(JSON.parse(from.content_json), JSON.parse(to.content_json)),
    };
}
/** Pure field-level diff over two parsed content documents. Exported so
 *  the SPA version-history view and V3-2-7 drift can reuse it without a
 *  database. */
export function diffContent(before, after) {
    const beforeLeaves = new Map();
    const afterLeaves = new Map();
    flattenLeaves(before, '', beforeLeaves);
    flattenLeaves(after, '', afterLeaves);
    const paths = Array.from(new Set([...beforeLeaves.keys(), ...afterLeaves.keys()])).sort();
    const changes = [];
    for (const path of paths) {
        const inBefore = beforeLeaves.has(path);
        const inAfter = afterLeaves.has(path);
        const b = beforeLeaves.get(path);
        const a = afterLeaves.get(path);
        if (inBefore && !inAfter) {
            changes.push({ path, change: 'removed', before: b, after: null });
        }
        else if (!inBefore && inAfter) {
            changes.push({ path, change: 'added', before: null, after: a });
        }
        else if (canonicalJson(b) !== canonicalJson(a)) {
            changes.push({ path, change: 'changed', before: b, after: a });
        }
    }
    return changes;
}
/**
 * Build a disclosure draft for a system from its latest snapshot's
 * ACCEPTED component classifications. Read-only; workspace-scoped.
 */
export async function draftFromComponents(db, workspaceId, systemId) {
    const sysRow = (await db.query(`SELECT s.name FROM ai_systems s
       JOIN customer_profiles p ON p.profile_id = s.profile_id
      WHERE s.system_id = ? AND p.workspace_id = ?`, [systemId, workspaceId])).results[0];
    const systemName = sysRow?.name ?? 'AI system';
    // Accepted components of the latest snapshot, joined to the catalog
    // entry for the provider. Only 'accepted' rows feed the draft.
    const rows = (await db.query(`SELECT c.name, c.version, c.ai_role, cat.provider_id
       FROM ai_system_components c
       LEFT JOIN ai_component_catalog cat ON cat.entry_id = c.catalog_entry_id
      WHERE c.workspace_id = ? AND c.system_id = ?
        AND c.classification_status = 'accepted'
        AND c.snapshot_id = (
          SELECT snapshot_id FROM ai_system_bom_snapshots
           WHERE workspace_id = ? AND system_id = ?
           ORDER BY published_at DESC LIMIT 1
        )`, [workspaceId, systemId, workspaceId, systemId])).results;
    const providerSet = new Set();
    const models = [];
    for (const r of rows) {
        if (r.provider_id)
            providerSet.add(r.provider_id);
        if (r.ai_role === 'model_ref' || r.ai_role === 'model_file') {
            models.push({
                name: r.name,
                version: r.version ?? 'Template',
                provider: r.provider_id ?? 'Template',
                hosting_mode: 'third_party_api',
                purpose: 'Template',
            });
        }
    }
    const providers = [...providerSet].map((name) => ({
        name,
        domain: 'Template',
        data_sent: 'Template',
        region: 'Template',
    }));
    const content = {
        scope: { products_services: [systemName], description: 'Drafted from the AI system component inventory (ADR-0025).' },
        models,
        providers,
        training_data_classes: [], // Template — the SBOM cannot know this
        retention_and_training_use: null,
        human_oversight: null,
        incident: null,
        frameworks_claimed: null,
    };
    // ADR-0092 §1: the APPROVED declared authority (never a draft) is OFFERED
    // beside the draft, never merged into it — tool names, prohibited actions and
    // limits leave the supplier only when they choose to add them before publishing.
    const authority = (await db.query(`SELECT version, tools_json, action_ceiling_json, containment_json FROM ai_system_authority
      WHERE workspace_id = ? AND system_id = ? AND status = 'active' ORDER BY version DESC LIMIT 1`, [workspaceId, systemId])).results[0];
    const validation = validateDisclosureContent(content);
    const gaps = validation.valid ? [] : [...new Set(validation.errors.map((e) => e.path.split('[')[0] || 'root'))];
    return {
        content,
        providers: [...providerSet],
        models: models.map((m) => m.name),
        publishable: validation.valid,
        gaps,
        authority_prefill: authority
            ? { from_authority_version: authority.version, groups: disclosureAuthorityFromDeclaration(authority) }
            : null,
    };
}
//# sourceMappingURL=ai-disclosure-service.js.map